import React, { useEffect, useState, Suspense, lazy } from "react";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LoadingSpinner } from "./components/ui/LoadingSpinner";
import { showToast } from "./lib/toast";
import { 
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  Outlet,
  useParams,
} from "react-router-dom";

import Sidebar from "./components/layout/Sidebar";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { FlagKey, isFlagEnabled } from "./lib/featureFlags";
import Footer from "./components/layout/Footer";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import CookieConsent from "./components/CookieConsent";
import TosClickwrapModal from "./components/TosClickwrapModal";
import CompanyProfilePrompt from "./components/CompanyProfilePrompt";
import { hasAcceptedCurrentTos } from "./lib/tos";
import { loginRedirectPath } from "./lib/loginRedirect";
import { apiHealth } from "./lib/api/client";
import ProductUpdatesProvider, { useProductUpdates } from "./components/product-updates/ProductUpdatesProvider";

const queryClient = new QueryClient()

// Lazy load all pages for code splitting
const LoginPage = lazy(() => import("./pages/public/Login/LoginPage"));
const DashboardPage = lazy(() => import("./pages/startup/Dashboard/DashboardPage"));
const SettingsPage = lazy(() => import("./pages/startup/Settings/SettingsPage"));
const VCSettingsPage = lazy(() => import("./pages/vc/Settings/VCSettingsPage"));
const VCDashboardPage = lazy(() => import("./pages/vc/Dashboard/VCDashboardPage"));
const VCCompanyDeepDivePage = lazy(
  () => import("./pages/vc/CompanyDeepDive/VCCompanyDeepDivePage"),
);
const SharedDataRoomPage = lazy(() => import("./pages/public/SharedDataRoom/SharedDataRoomPage"));
const ViewReportPage = lazy(() => import( "./pages/public/ViewReport/ViewReport"));
const ResetPasswordPage = lazy(() => import("./pages/public/ResetPassword/ResetPassword"));
const VCReportRequestsPage = lazy(
  () => import("./pages/vc/Reporting/VCReportRequestsPage"),
);
const VCPortfolioReportsPage = lazy(() => import("./pages/vc/PortfolioReports/PortfolioReportsPage"));
const ViewPortfolioReportPage = lazy(() => import("./pages/vc/PortfolioReports/ViewPortfolioReportPage"));
const SharedPortfolioReportPage = lazy(() => import("./pages/public/SharedPortfolioReport/SharedPortfolioReportPage"));
const VCAnalyticsPage = lazy(() => import("./pages/vc/Analytics/AnalyticsPage"));
const VCDataAccessPage = lazy(() => import("./pages/vc/DataAccess/VCDataAccessPage"));
const ConnectionsPage = lazy(() => import("./pages/startup/Connections/ConnectionsPage"));
const InvestorsPage = lazy(() => import("./pages/startup/Investors/InvestorsPage"));
const ReportingPage = lazy(() => import("./pages/startup/Reporting/ReportingPage"));
const StartupDataAccessPage = lazy(() => import("./pages/startup/DataAccess/AccessRequestsPanel"));
const VaultPage = lazy(() => import("./pages/startup/Vault/VaultPage"));
const NotFoundPage = lazy(() => import("./pages/public/NotFound/NotFoundPage"));
const UnauthorizedPage = lazy(() => import("./pages/public/Unauthorized/UnauthorizedPage"));
const PrivacyPage = lazy(() => import("./pages/public/Privacy/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/public/Terms/TermsPage"));
const AcceptInvitePage = lazy(() => import("./pages/public/AcceptInvite/AcceptInvitePage"));
const OAuthConsentPage = lazy(() => import("./pages/public/OAuthConsent/OAuthConsentPage"));
const OAuthCallbackPage = lazy(() => import("./pages/public/OAuthCallback/OAuthCallbackPage"));
const CompanyOnboardingPage = lazy(
  () => import("./pages/public/CompanyOnboarding/CompanyOnboardingPage"),
);

// Blocks a route whose feature is flagged off, so a pasted URL cannot walk
// around a hidden nav item. `hidden` sends every visitor, signed in or not, where
// an unknown URL goes, for a feature that should not look like it exists; it
// has to wrap the auth guard so that guard's redirects don't give the route
// away. With no children it acts as a layout route.
const FlagRoute = ({
  flag,
  hidden = false,
  children,
}: {
  flag: FlagKey;
  hidden?: boolean;
  children?: React.ReactElement;
}) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner fullPage />;
  if (!isFlagEnabled(user, flag)) {
    if (hidden) return <Navigate to="/404" replace />;
    return <Navigate to={user?.company.mode === "vc" ? "/vc" : "/startup"} replace />;
  }
  return children ?? <Outlet />;
};

// Protected routes wrapper
const ProtectedRoutes = ({
  allowedMode,
}: {
  allowedMode: "vc" | "startup";
}) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingSpinner fullPage />;
  if (!user) return <Navigate to="/login" replace />;

  // Check for mode mismatch.
  if (user.company.mode !== allowedMode)
    return <Navigate to="/unauthorized" replace />;

  return <Outlet />;
};

// --- Wrapper components for route params ---
const VCCompanyDeepDiveWrapper = () => {
  const { companyId } = useParams();
  return <VCCompanyDeepDivePage companyId={companyId || ""} />;
};

const SharedDataRoomWrapper = () => {
  const { shareToken } = useParams();
  return <SharedDataRoomPage shareToken={shareToken || ""} />;
};

const AppContent = () => {
  const { user, loading: authLoading } = useAuth();
  const productUpdates = useProductUpdates();
  const navigate = useNavigate();
  const location = useLocation();

  // Tab title reflects which persona is signed in, since the app has no single
  // "dashboard" — an investor and a company see entirely different route trees.
  useEffect(() => {
    document.title = !user
      ? "Vein"
      : user.company.mode === "vc"
        ? "Vein | Investor"
        : "Vein | Company";
  }, [user]);

  // Setup keyboard shortcuts
  const [lastGPress, setLastGPress] = useState<number>(0);
  // Sidebar collapsed state lives here rather than in Sidebar because the main
  // content margin has to move with it.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    () => localStorage.getItem("vein-sidebar-collapsed") === "true",
  );
  useEffect(() => {
    // Skip keyboard shortcuts for public verify pages
    if (location.pathname.startsWith("/verify/")) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isTyping =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (isTyping) return;

      // Sidebar collapse — bracket keys, matching the direction they point.
      if (event.key === "[" || event.key === "]") {
        event.preventDefault();
        setSidebarCollapsed(() => {
          const next = event.key === "[";
          localStorage.setItem("vein-sidebar-collapsed", String(next));
          return next;
        });
        return;
      }

      // 'g' sequence shortcuts
      if (event.key === "g") {
        const now = Date.now();
        if (now - lastGPress < 500) {
          // Second 'g' press within 500ms - wait for next key
          setLastGPress(0);
        } else {
          setLastGPress(now);
        }
        return;
      }

      // Check if we're in a 'g' sequence
      if (Date.now() - lastGPress < 500) {
        event.preventDefault();
        if (event.key === "d") {
          navigate("/startup");
          setLastGPress(0);
        } else if (event.key === "p") {
          navigate("/vc");
          setLastGPress(0);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lastGPress, navigate, location]);

  // Basic API health check on load
  useEffect(() => {
    if (location.pathname.startsWith("/verify/")) return;
    (async () => {
      const ok = await apiHealth();
      if (!ok) {
        showToast.errorMessage("We're having trouble reaching Vein. Some features may not work until the connection is back.");
      }
    })();
  }, [location]);


  // Show loading while checking auth
  if (authLoading) {
    return <LoadingSpinner fullPage />;
  }

  // Layout logic: Decisions on what to show
  // We check if the current path mode matches the user's mode to prevent flashing
  // a dashboard skeleton for a mode they don't have access to before the redirect kicks in.
  const mode = user?.company?.mode || "startup";

  const showSidebar = location.pathname.startsWith("/vc") || location.pathname.startsWith("/startup")

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      localStorage.setItem("vein-sidebar-collapsed", String(!prev));
      return !prev;
    });
  };

  // ToS clickwrap gate: any logged-in user who hasn't accepted the current ToS
  // version must accept before using the app (first login or after a version bump).
  const needsTosAcceptance = !!user && !hasAcceptedCurrentTos(user.tosVersion) && (location.pathname.startsWith("/vc") || location.pathname.startsWith("/startup"));
  // Announcements and feedback belong to a company's workspace, so they wait until the user has one.
  const hasCompany = !!user?.company?.id;

  return (
    <div className="h-screen w-screen bg-vein-ground text-vein-text-primary font-sans antialiased flex overflow-x-hidden">
      {/* Sidebar - only shown when logged in, not on public pages, and in the correct mode area */}
      {showSidebar && (
        <Sidebar
          mode={mode}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapsed}
        />
      )}
      {/* Main Content Area */}
      <div
        className={`flex-1 flex flex-col overflow-x-hidden transition-all duration-500 ease-out ${
          showSidebar ? (sidebarCollapsed ? "ml-[92px]" : "ml-64") : ""
        } max-w-full`}
      >
        <main className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide transition-all duration-500 ease-out">
          <div className="min-h-full flex flex-col">
            <div className="flex-1">
              <Suspense fallback={<LoadingSpinner fullPage />}>
                <Routes>
                  {/* VC Routes Group */}
                  <Route element={<ProtectedRoutes allowedMode="vc" />}>
                    <Route path="/vc">
                      <Route index element={<VCAnalyticsPage />} />
                      <Route
                        path="companies/:companyId"
                        element={<VCCompanyDeepDiveWrapper />}
                      />
                      <Route path="portfolio" element={<VCDashboardPage />} />
                      <Route
                        path="reporting"
                        element={<VCReportRequestsPage />}
                      />
                      <Route path="settings" element={<VCSettingsPage />} />
                      <Route
                        path="data-access"
                        element={
                          <FlagRoute flag="public_api">
                            <VCDataAccessPage />
                          </FlagRoute>
                        }
                      />
                    </Route>
                  </Route>

                  {/* Startup Routes Group */}
                  <Route element={<ProtectedRoutes allowedMode="startup" />}>
                    <Route path="/startup">
                      <Route index element={<DashboardPage />} />
                      <Route path="investors" element={<InvestorsPage />} />
                      {/* Renamed from "Cap Table" — an accelerator relationship
                          involves no equity, so the old name was wrong for it. */}
                      <Route
                        path="cap-table"
                        element={<Navigate to="/startup/investors" replace />}
                      />
                      <Route path="connections" element={<ConnectionsPage />} />
                      <Route path="reporting/*" element={<ReportingPage />} />
                      <Route path="vault" element={<VaultPage />} />
                      <Route
                        path="data-access"
                        element={
                          <FlagRoute flag="public_api">
                            <StartupDataAccessPage />
                          </FlagRoute>
                        }
                      />
                      <Route path="settings" element={<SettingsPage />} />
                    </Route>
                  </Route>

                  <Route
                    path="/report/:reportId"
                    element={<ViewReportPage />}
                  />
                  
                  <Route
                    path="/share/:shareToken"
                    element={<SharedDataRoomWrapper />}
                  />

                  <Route element={<FlagRoute flag="portfolio_reports" hidden />}>
                    <Route element={<ProtectedRoutes allowedMode="vc" />}>
                      <Route path="/vc/portfolio-reports" element={<VCPortfolioReportsPage />} />
                      <Route path="/portfolio-report/:reportId" element={<ViewPortfolioReportPage />} />
                    </Route>
                  </Route>
                  <Route path="/portfolio-share/:shareToken" element={<SharedPortfolioReportPage />} />

                  {/* Legal Pages */}
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />

                  {/* Team invite acceptance — no auth guard; page handles redirect */}
                  <Route path="/join" element={<AcceptInvitePage />} />
                  <Route path="/join/company" element={<CompanyOnboardingPage />} />
                  <Route path="/oauth/consent" element={<OAuthConsentPage />} />

                  {/* Google OAuth redirect target — no auth guard, page handles both
                      the signed-out (sign-in/invite) and signed-in (link) cases */}
                  <Route path="/auth/callback" element={<OAuthCallbackPage />} />

                  {/* Public Login Route */}
                  <Route
                    path="/login"
                    element={
                      user ? (
                        // Logging in sets the user before LoginForm navigates, so
                        // this redirect has to honor ?redirect= as well.
                        <Navigate to={loginRedirectPath(location.search) ?? "/"} replace />
                      ) : (
                        <div className="w-full h-full">
                          <LoginPage />
                        </div>
                      )
                    }
                  />

                  <Route
                    path="/reset-password"
                    element={
                      <div className="w-full h-full">
                        <ResetPasswordPage />
                      </div>
                    }
                  />

                  {/* Redirect Root based on mode */}
                  <Route
                    path="/"
                    element={
                      user ? (
                        <Navigate to={`/${mode}`} replace />
                      ) : (
                        <Navigate to="/login" replace />
                      )
                    }
                  />

                  {/* Error Pages */}
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  <Route path="/404" element={<NotFoundPage />} />
                  <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
              </Suspense>
            </div>
            {/* Footer - hidden on login, data room, and verify pages */}
            {!["/login"].includes(location.pathname) &&
              !location.pathname.startsWith("/share/") &&
              !location.pathname.startsWith("/oauth/") &&
              !location.pathname.startsWith("/verify/") && 
              !location.pathname.startsWith("/report/") && <Footer
                onOpenUpdates={showSidebar && hasCompany && !needsTosAcceptance ? productUpdates?.openUpdates : undefined}
                hasUnreadUpdates={productUpdates?.hasUnread}
              />}
          </div>
        </main>
      </div>
      {needsTosAcceptance && <TosClickwrapModal />}
      {/* Gated on the clickwrap so the two can never stack. The prompt decides
          for itself whether it applies — empty profile, admin+, not dismissed. */}
      {!needsTosAcceptance && location.pathname.startsWith("/startup") && <CompanyProfilePrompt onBlockingChange={productUpdates?.setOnboardingPending} />}
    </div>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path="/*"
      element={
        <AuthProvider>
          <ProductUpdatesProvider>
            <AppContent />
          </ProductUpdatesProvider>
        </AuthProvider>
      }
    />,
  ),
);

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        {/* Global components */}
        {/* Cookie consent banner + analytics (analytics only loads after consent) */}
        <CookieConsent />
        {/* Toasts render through `toast.custom` (src/lib/toast.ts), so the card
            styling lives in the Toast component — nothing to configure here
            beyond placement. */}
        <Toaster position="bottom-right" gutter={12} containerClassName="!bottom-6 !right-6" />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
